/**
 * Polynomial Graph Utilities
 *
 * Core functions for parsing, evaluating, and generating polynomial graph variations
 * for the hw_questions polynomial graph selection feature.
 */

import { createSharedYCompression } from '../shared/graphScaling';

// ============================================
// CONSTANTS & DEFAULTS
// ============================================

export const MAX_POLYNOMIAL_DEGREE = 10;
export const MAX_FACTOR_MULTIPLICITY = 5;

// A y-intercept question is only meaningful when the graph crosses the y-axis
// away from the origin. Roots come from exact integer/fraction arithmetic, so a
// genuine root-at-0 is exactly 0; this epsilon only absorbs float noise.
export const Y_INTERCEPT_MIN_ABS = 1e-9;

export const defaultPolynomialGraphConfig = {
  type: 'polynomialGraphSelection',
  equation: '',
  parsedPolynomial: null,
  interceptType: 'x',
  grid: {
    xMin: -10,
    xMax: 10,
    yMin: -10,
    yMax: 10,
    xStep: 1,
    yStep: 1,
    showGridLines: true,
    showAxisLabels: true,
  },
  interceptTolerance: 0.01,
  variationSeed: null,
  correctGraphIndex: 0,
};

// ============================================
// PARSING UTILITIES
// ============================================

/**
 * Parse a fraction string like "1/2", "-3/4", or regular numbers
 * IMPORTANT: Use this for both admin and student input parsing
 *
 * @param {string|number} str - Input string (e.g., "1/2", "-3", "2.5", "+3")
 * @returns {number} Parsed numeric value
 */
export function parseFraction(str) {
  if (typeof str === 'number') return str;
  if (!str || typeof str !== 'string') return NaN;

  const trimmed = str.trim();

  // Handle fractions: "1/2", "-3/4", "1/6"
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length !== 2) return NaN;
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (den === 0) return NaN;
    return num / den;
  }

  // Handle regular numbers (including with +/- prefix)
  return parseFloat(trimmed);
}

/**
 * Convert a LaTeX-formatted equation into the plain syntax the parser expects.
 *
 * Equations captured via the "crop question" AI/OCR flow arrive as LaTeX
 * (e.g. "f(x) = \frac{1}{6}(x+3)(x-2)^{2}"), which the preview renders correctly
 * but the parser could not read — the leading coefficient was silently dropped.
 * Normalizing here lets OCR'd LaTeX parse identically to typed input.
 *
 * @param {string} equation - Raw equation, possibly containing LaTeX
 * @returns {string} Plain-syntax equation (e.g. "f(x) = 1/6(x+3)(x-2)^2")
 */
export function normalizeLatexEquation(equation) {
  if (!equation || typeof equation !== 'string') return equation || '';

  let s = equation;
  // \left( ... \right) -> ( ... )
  s = s.replace(/\\left|\\right/g, '');
  // Explicit multiplication markers -> nothing (parser uses juxtaposition)
  s = s.replace(/\\cdot|\\times/g, '');
  // Spacing macros: \, \; \! \: and escaped space
  s = s.replace(/\\[,;!: ]/g, '');
  // \frac{a}{b} / \dfrac / \tfrac:
  //  - numeric/numeric        → "a/b" (a fraction coefficient, e.g. \frac{1}{6})
  //  - polynomial/numeric     → "1/b a" (e.g. \frac{(x+3)(x-2)}{6} = 1/6(x+3)(x-2) —
  //    keeping it as "(x+3)(x-2)/6" silently lost the 1/6 scale)
  //  - anything else          → left for the parser's residual check to reject
  const NUMERIC = /^[+-]?(\d+(?:\.\d+)?|\.\d+)$/;
  s = s.replace(/\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/gi, (m, a, b) => {
    if (NUMERIC.test(a.trim()) && NUMERIC.test(b.trim())) return `${a}/${b}`;
    if (NUMERIC.test(b.trim())) return `1/${b.trim()}${a}`;
    // Not a polynomial (e.g. a rational \frac{(x+1)}{(x-2)}): keep the '/' so the
    // parser's residual check rejects it — the catch-all below must not silently
    // strip the \frac and leave a plausible-looking product.
    return `${a}/${b}`;
  });
  // Exponent braces: ^{2} -> ^2
  s = s.replace(/\^\s*\{([^{}]*)\}/g, '^$1');
  // Drop any remaining LaTeX commands and stray braces
  s = s.replace(/\\[a-zA-Z]+/g, '').replace(/[{}]/g, '');
  return s;
}

/**
 * Parse a factored polynomial equation string
 *
 * Supported formats:
 * - "f(x) = (x+3)(x-2)"
 * - "f(x) = 1/6(x+3)(x-2)^2"
 * - "f(x) = \frac{1}{6}(x+3)(x-2)^{2}"  (LaTeX, e.g. from the crop-question tool)
 * - "f(x) = -(x+1)^2(x-3)"
 * - "f(x) = -1/4(x+2)(x-1)^3"
 * - "f(x) = -x(x-3)^2"                  (bare "x" factor = a root at 0)
 * - "y = 2(x-1)(x+2)(x-3)"
 *
 * @param {string} equation - The equation string to parse
 * @returns {Object} Parsed polynomial object
 * @throws {Error} If equation cannot be parsed
 */
export function parseFactoredPolynomial(equation) {
  // 1. Normalize: convert LaTeX to plain syntax, then remove spaces, lowercase
  let normalized = normalizeLatexEquation(equation).replace(/\s+/g, '').toLowerCase();

  // 2. Extract right-hand side (after '=')
  const eqMatch = normalized.match(/[fy]\(x\)=(.+)/) || normalized.match(/y=(.+)/);
  if (!eqMatch) {
    throw new Error('Equation must be in form f(x)=... or y=...');
  }
  let rhs = eqMatch[1];

  // 3. Extract leading coefficient
  // Match: optional sign, optional number/fraction, before the first factor —
  // which is either a '(' or a bare 'x' (e.g. "-x(x-3)^2", "2x(x-1)").
  const leadMatch = rhs.match(/^([+-])?(\d+(?:\/\d+)?(?:\.\d+)?)?(?=[(x])/);
  let leadingCoefficient = 1;
  if (leadMatch) {
    const sign = leadMatch[1] === '-' ? -1 : 1;
    if (leadMatch[2]) {
      leadingCoefficient = sign * parseFraction(leadMatch[2]);
    } else {
      leadingCoefficient = sign;
    }
  }

  // 4. Extract factors: parenthesized (x+a)^n / (x-a)^n, or the bare variable
  // "x"^n (a root at 0, e.g. "-x(x-3)^2" or "x^2(x-3)"). The bare-x alternative
  // only fires where a parenthesized factor hasn't already consumed the 'x'.
  const factorRegex = /\(x([+-][\d./]+)\)(?:\^(\d+))?|x(?:\^(\d+))?/g;
  const factors = [];
  let match;

  while ((match = factorRegex.exec(rhs)) !== null) {
    if (match[1] !== undefined) {
      const constant = parseFraction(match[1]); // e.g., "+3" → 3, "-2" → -2
      const root = -constant;                    // root is negative of constant
      const multiplicity = match[2] ? parseInt(match[2]) : 1;
      factors.push({ root, multiplicity });
    } else {
      // Bare "x" factor → root at 0
      const multiplicity = match[3] ? parseInt(match[3]) : 1;
      factors.push({ root: 0, multiplicity });
    }
  }

  if (factors.length === 0) {
    throw new Error('No valid factors found. Use format (x+a) or (x-a)^n');
  }

  // 4b. Residual check: everything in the RHS must be either the leading
  // coefficient (stripped first, from the front only — a trailing "+3" is NOT a
  // coefficient) or a recognized factor. Silently ignoring leftovers (a trailing
  // "/6", an unconverted \frac, a bare "+3" term, …) produced wrong graphs.
  const residual = rhs
    .replace(/^([+-])?(\d+(?:\/\d+)?(?:\.\d+)?)?(?=[(x])/, '')
    .replace(/\(x[+-][\d./]+\)(?:\^\d+)?/g, '')
    .replace(/x(?:\^\d+)?/g, ''); // bare "x" factors (root at 0); parenthesized
                                  // factors already stripped above, so the only
                                  // 'x' left here is a legitimate bare factor
  if (residual !== '') {
    throw new Error(
      `Could not parse "${residual}". Enter the polynomial in factored form, e.g. 1/6(x+3)(x-2)^2`
    );
  }

  // 5. Validate multiplicity limits
  for (const factor of factors) {
    if (factor.multiplicity > MAX_FACTOR_MULTIPLICITY) {
      throw new Error(`Multiplicity ${factor.multiplicity} exceeds maximum of ${MAX_FACTOR_MULTIPLICITY}`);
    }
  }

  // 6. Calculate and validate degree
  const degree = factors.reduce((sum, f) => sum + f.multiplicity, 0);
  if (degree > MAX_POLYNOMIAL_DEGREE) {
    throw new Error(`Polynomial degree ${degree} exceeds maximum of ${MAX_POLYNOMIAL_DEGREE}`);
  }

  // 7. Compute expanded coefficients
  const expandedCoefficients = expandFactorsToCoefficients(factors, leadingCoefficient);

  // 8. Calculate y-intercept
  const yIntercept = expandedCoefficients[0];

  return {
    leadingCoefficient,
    factors,
    degree,
    expandedCoefficients,
    yIntercept,
    originalEquation: equation,
  };
}

// ============================================
// POLYNOMIAL OPERATIONS
// ============================================

/**
 * Expand polynomial factors to coefficient array
 * Uses iterative multiplication by linear factors
 *
 * @param {Array} factors - Array of {root, multiplicity}
 * @param {number} leadingCoeff - Leading coefficient
 * @returns {number[]} Coefficients [a0, a1, ..., an] in ascending power order
 */
export function expandFactorsToCoefficients(factors, leadingCoeff) {
  // Start with [1] representing constant 1
  let coeffs = [1];

  for (const { root, multiplicity } of factors) {
    // Multiply by (x - root) 'multiplicity' times
    for (let i = 0; i < multiplicity; i++) {
      coeffs = multiplyByLinearFactor(coeffs, -root);
    }
  }

  // Scale by leading coefficient
  return coeffs.map(c => c * leadingCoeff);
}

/**
 * Multiply polynomial coefficients by (x + c)
 *
 * @param {number[]} coeffs - Current coefficients [a0, a1, ...]
 * @param {number} c - Constant in (x + c)
 * @returns {number[]} New coefficients after multiplication
 */
function multiplyByLinearFactor(coeffs, c) {
  const result = new Array(coeffs.length + 1).fill(0);
  for (let i = 0; i < coeffs.length; i++) {
    result[i] += coeffs[i] * c;      // Contribution from constant term
    result[i + 1] += coeffs[i];       // Contribution from x term
  }
  return result;
}

/**
 * Evaluate polynomial at a given x value
 * Uses Horner's method for numerical stability
 *
 * @param {Object} polynomial - The polynomial to evaluate
 * @param {number} x - The x value
 * @returns {number} f(x)
 */
export function evaluatePolynomialAt(polynomial, x) {
  const { expandedCoefficients } = polynomial;
  let result = 0;
  for (let i = expandedCoefficients.length - 1; i >= 0; i--) {
    result = result * x + expandedCoefficients[i];
  }
  return result;
}

// ============================================
// GRAPH VARIATION GENERATION
// ============================================

/**
 * Whether a polynomial supports a y-intercept question.
 *
 * A polynomial that passes through the origin (has a root at 0, so f(0) = 0)
 * has its y-intercept on the x-axis: the question is pedagogically degenerate
 * and the distractor builders collapse to the zero polynomial. Such questions
 * must use x-intercept mode instead.
 *
 * Root-based (not yIntercept-based) so it still works on server round-tripped
 * configs, which keep factors but strip the computed yIntercept.
 *
 * @param {Object} polynomial - Parsed polynomial ({ factors: [{root}] })
 * @returns {boolean} true if y-intercept mode is valid for this polynomial
 */
export function canUseYInterceptMode(polynomial) {
  if (!polynomial || !Array.isArray(polynomial.factors) || polynomial.factors.length === 0) {
    return false;
  }
  const hasRootAtZero = polynomial.factors.some(
    (f) => Math.abs(f.root) < Y_INTERCEPT_MIN_ABS
  );
  return !hasRootAtZero;
}

/**
 * Generate 4 graph variations based on intercept type
 *
 * For X-intercepts:
 * - All graphs pass through same x-intercept points
 * - Vary by: leading coefficient sign, multiplicity swaps
 *
 * For Y-intercept:
 * - All graphs pass through same y-intercept point
 * - Vary by: leading coefficient sign, horizontal shifts
 *
 * @param {Object} polynomial - The original polynomial
 * @param {'x' | 'y'} interceptType - Which intercept type
 * @param {number} seed - Random seed for consistent shuffling
 * @returns {{ variations: Array, correctIndex: number }}
 */
export function generateGraphVariations(polynomial, interceptType, seed) {
  let variations = [];

  if (interceptType === 'x') {
    variations = generateXInterceptVariations(polynomial);
  } else {
    // Hard invariant: y-intercept variations divide by the product of the
    // (negated) roots and scale by the y-intercept, so a polynomial through the
    // origin (f(0) = 0, e.g. "-x(x-3)^2") collapses distractors to the zero
    // polynomial. Reject it here; callers should route such questions to
    // x-intercept mode (see canUseYInterceptMode).
    if (!canUseYInterceptMode(polynomial)) {
      throw new Error(
        'Y-intercept mode is unavailable when the polynomial passes through the origin (f(0) = 0). Use X-intercepts instead.'
      );
    }
    variations = generateYInterceptVariations(polynomial);
  }

  // Shuffle with seeded random
  const { shuffled, originalIndex } = seededShuffle(variations, seed);

  return {
    variations: shuffled.map((v, i) => ({
      ...v,
      label: ['A', 'B', 'C', 'D'][i],
      isCorrect: i === originalIndex,
    })),
    correctIndex: originalIndex,
  };
}

/**
 * Generate variations for X-intercept questions
 * All variations pass through the same x-intercepts
 */
function generateXInterceptVariations(polynomial) {
  const { factors, leadingCoefficient } = polynomial;

  // Variation 1: Original (correct)
  const original = polynomial;

  // Variation 2: Flip leading coefficient sign
  const flipped = buildPolynomialFromFactors(
    factors,
    -leadingCoefficient
  );

  // Variation 3: Swap multiplicities between first two distinct factors
  const swapped = buildPolynomialFromFactors(
    swapMultiplicities(factors),
    leadingCoefficient
  );

  // Variation 4: Both flip and swap
  const flippedSwapped = buildPolynomialFromFactors(
    swapMultiplicities(factors),
    -leadingCoefficient
  );

  return [
    { polynomial: original, variationType: 'original' },
    { polynomial: flipped, variationType: 'flipped' },
    { polynomial: swapped, variationType: 'swapped' },
    { polynomial: flippedSwapped, variationType: 'flipped_swapped' },
  ];
}

/**
 * Generate variations for Y-intercept questions
 * All variations pass through the same y-intercept (0, k)
 */
function generateYInterceptVariations(polynomial) {
  // Variation 1: Original (correct)
  const original = polynomial;

  // Variation 2: Flip leading coefficient, adjust constant to maintain y-intercept
  const flipped = buildFlippedWithSameYIntercept(polynomial);

  // Variation 3: Shift roots horizontally, adjust coefficient to maintain y-intercept
  const shifted = buildShiftedPolynomialWithYIntercept(polynomial, 1.5);

  // Variation 4: Change multiplicities but adjust coefficient to maintain y-intercept
  const reshaped = buildReshapedPolynomialWithYIntercept(polynomial);

  return [
    { polynomial: original, variationType: 'original' },
    { polynomial: flipped, variationType: 'flipped' },
    { polynomial: shifted, variationType: 'shifted' },
    { polynomial: reshaped, variationType: 'reshaped' },
  ];
}

/**
 * Build polynomial with flipped sign but same y-intercept
 *
 * EDGE CASE: If first root is 0 (or very close to 0), negating it produces
 * the same root, resulting in an identical variation. In this case, we find
 * a non-zero root to negate, or shift the root if all roots are zero.
 */
function buildFlippedWithSameYIntercept(polynomial) {
  const { factors, yIntercept } = polynomial;

  // Find the first non-zero root to negate (to ensure visual difference)
  let indexToNegate = factors.findIndex(f => Math.abs(f.root) > 0.01);

  // If all roots are zero or very close to zero, shift a root instead
  if (indexToNegate === -1) {
    // All roots are ~0, so create a different polynomial by shifting
    const newFactors = factors.map((f, i) => ({
      root: i === 0 ? f.root + 2 : f.root, // Shift first root by 2
      multiplicity: f.multiplicity,
    }));

    const productOfNegRoots = newFactors.reduce((prod, f) =>
      prod * Math.pow(-f.root, f.multiplicity), 1
    );
    const newLeadCoef = productOfNegRoots !== 0
      ? yIntercept / productOfNegRoots
      : -polynomial.leadingCoefficient;

    return buildPolynomialFromFactors(newFactors, newLeadCoef);
  }

  // Negate the selected non-zero root
  const newFactors = factors.map((f, i) => {
    if (i === indexToNegate) {
      return { root: -f.root, multiplicity: f.multiplicity };
    }
    return { ...f };
  });

  // Calculate new leading coefficient to maintain y-intercept
  const productOfNegRoots = newFactors.reduce((prod, f) =>
    prod * Math.pow(-f.root, f.multiplicity), 1
  );
  const newLeadCoef = productOfNegRoots !== 0
    ? yIntercept / productOfNegRoots
    : -polynomial.leadingCoefficient;

  return buildPolynomialFromFactors(newFactors, newLeadCoef);
}

/**
 * Build polynomial with shifted roots but same y-intercept
 */
function buildShiftedPolynomialWithYIntercept(polynomial, shiftAmount) {
  const { factors, yIntercept } = polynomial;

  // Shift all roots by the amount
  const newFactors = factors.map(f => ({
    root: f.root + shiftAmount,
    multiplicity: f.multiplicity,
  }));

  // Calculate new leading coefficient to maintain y-intercept
  const productOfNegRoots = newFactors.reduce((prod, f) =>
    prod * Math.pow(-f.root, f.multiplicity), 1
  );
  const newLeadCoef = productOfNegRoots !== 0
    ? yIntercept / productOfNegRoots
    : polynomial.leadingCoefficient;

  return buildPolynomialFromFactors(newFactors, newLeadCoef);
}

/**
 * Build polynomial with changed multiplicities but same y-intercept
 */
function buildReshapedPolynomialWithYIntercept(polynomial) {
  const { factors, yIntercept } = polynomial;

  // Change multiplicities while keeping roots
  const newFactors = factors.map((f, i) => ({
    root: f.root,
    multiplicity: Math.max(1, f.multiplicity + (i % 2 === 0 ? 1 : -1)),
  }));

  // Calculate new leading coefficient to maintain y-intercept
  const productOfNegRoots = newFactors.reduce((prod, f) =>
    prod * Math.pow(-f.root, f.multiplicity), 1
  );
  const newLeadCoef = productOfNegRoots !== 0
    ? yIntercept / productOfNegRoots
    : polynomial.leadingCoefficient;

  return buildPolynomialFromFactors(newFactors, newLeadCoef);
}

/**
 * Swap multiplicities to create visually different graph behavior
 *
 * EDGE CASE HANDLING:
 * - Single factor: Change multiplicity (1->2 or 2->1) to alter cross/bounce behavior
 * - All same multiplicities: Increment one to change parity
 */
function swapMultiplicities(factors) {
  const swapped = factors.map(f => ({ ...f }));

  // EDGE CASE: Single factor polynomial (e.g., f(x) = (x-2)^3)
  if (factors.length === 1) {
    // Toggle between odd and even multiplicity
    if (swapped[0].multiplicity % 2 === 1) {
      swapped[0].multiplicity += 1; // odd -> even (cross -> bounce)
    } else {
      swapped[0].multiplicity -= 1; // even -> odd (bounce -> cross)
      if (swapped[0].multiplicity < 1) swapped[0].multiplicity = 1;
    }
    return swapped;
  }

  // Find first pair with different multiplicities (or different parity)
  let swapPerformed = false;
  for (let i = 0; i < factors.length - 1; i++) {
    const m1 = factors[i].multiplicity;
    const m2 = factors[i + 1].multiplicity;

    // Prefer swapping if one is odd and one is even
    if ((m1 % 2) !== (m2 % 2)) {
      swapped[i].multiplicity = m2;
      swapped[i + 1].multiplicity = m1;
      swapPerformed = true;
      break;
    }
  }

  // EDGE CASE: All same multiplicities (e.g., f(x) = (x+1)(x-2) both mult=1)
  if (!swapPerformed) {
    swapped[0].multiplicity += 1;
  }

  return swapped;
}

/**
 * Build a polynomial from factors with given leading coefficient
 */
function buildPolynomialFromFactors(factors, leadingCoeff) {
  const expandedCoefficients = expandFactorsToCoefficients(factors, leadingCoeff);
  const degree = factors.reduce((sum, f) => sum + f.multiplicity, 0);

  return {
    leadingCoefficient: leadingCoeff,
    factors: factors.map(f => ({ ...f })),
    degree,
    expandedCoefficients,
    yIntercept: expandedCoefficients[0], // f(0) = a0
    originalEquation: null,
  };
}

// ============================================
// COORDINATE GRID INTEGRATION
// ============================================

/**
 * Convert parsed polynomial to CoordinateGrid curve element
 *
 * @param {Object} polynomial - Parsed polynomial object
 * @param {Object} gridBounds - Grid bounds { xMin, xMax, yMin, yMax }
 * @param {number} numSamples - Number of sample points (default 200)
 * @returns {Object} Curve element with pre-computed points array
 */
export function polynomialToCurveElement(polynomial, gridBounds, numSamples = 200) {
  return polynomialsToCurveElements([polynomial], gridBounds, numSamples)[0];
}

/**
 * Convert a GROUP of polynomials (a question's variation set) to curve elements
 * with ONE shared y-compression.
 *
 * High-degree curves overflow the window and collapse into spikes; a soft-clip
 * (identity inside the window, log compression of the overflow) keeps crosses,
 * bounces, and end behavior visible. The decision looks at each curve's root-
 * spanning region (so an ordinary cubic that only climbs steeply near the window
 * edges stays true-scale), and the same mapping is applied to EVERY curve in the
 * group — per-curve scaling drew four graphs sharing one true y-intercept at four
 * different heights, making y-intercept questions unanswerable.
 *
 * @param {Object[]} polynomials - Parsed polynomial objects
 * @param {Object} gridBounds - Grid bounds { xMin, xMax, yMin, yMax }
 * @param {number} numSamples - Number of sample points per curve (default 200)
 * @returns {Object[]} Curve elements, one per polynomial
 */
export function polynomialsToCurveElements(polynomials, gridBounds, numSamples = 200) {
  const { xMin = -10, xMax = 10 } = gridBounds || {};
  const step = (xMax - xMin) / numSamples;

  const makeElement = (polynomial, points, notToScale) => ({
    type: 'curve',
    curveType: 'polynomial',
    notToScale, // signals CoordinateGrid to drop misleading y tick labels
    parsed: {
      type: 'polynomial',
      degree: polynomial.degree,
      coefficients: polynomial.expandedCoefficients,
    },
    points, // CoordinateGrid's renderCurve expects this array
    id: `poly_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
  });

  if (!step || step <= 0) {
    return polynomials.map((p) => makeElement(p, [], false));
  }

  const rawPointArrays = polynomials.map((polynomial) => {
    const rawPoints = [];
    for (let x = xMin; x <= xMax; x += step) {
      const y = evaluatePolynomialAt(polynomial, x);
      if (!isNaN(y) && isFinite(y)) {
        rawPoints.push({ x, y });
      }
    }
    return rawPoints;
  });

  const focusRanges = polynomials.map((polynomial) => {
    const roots = (polynomial.factors || []).map((f) => f.root);
    return roots.length
      ? { xMin: Math.min(...roots) - 1, xMax: Math.max(...roots) + 1 }
      : undefined;
  });

  const { apply, compressed } = createSharedYCompression(rawPointArrays, gridBounds, focusRanges);
  return polynomials.map((p, i) => makeElement(p, apply(rawPointArrays[i]), compressed));
}

// ============================================
// GRID AUTO-SCALING HELPERS
// ============================================

/**
 * Suggest minimum X value based on polynomial roots
 */
export function suggestXMin(polynomial) {
  if (!polynomial?.factors?.length) return -10;
  const minRoot = Math.min(...polynomial.factors.map(f => f.root));
  return Math.floor(minRoot - 2);
}

/**
 * Suggest maximum X value based on polynomial roots
 */
export function suggestXMax(polynomial) {
  if (!polynomial?.factors?.length) return 10;
  const maxRoot = Math.max(...polynomial.factors.map(f => f.root));
  return Math.ceil(maxRoot + 2);
}

/**
 * Suggest minimum Y value based on polynomial behavior
 */
export function suggestYMin(polynomial) {
  if (!polynomial?.expandedCoefficients) return -10;

  const roots = polynomial.factors.map(f => f.root).sort((a, b) => a - b);
  const samplePoints = [
    ...roots,
    ...roots.slice(0, -1).map((r, i) => (r + roots[i + 1]) / 2),
    roots[0] - 1,
    roots[roots.length - 1] + 1,
  ];

  const yValues = samplePoints.map(x => evaluatePolynomialAt(polynomial, x));
  const minY = Math.min(...yValues, polynomial.yIntercept);

  return Math.max(-50, Math.floor(minY * 1.2));
}

/**
 * Suggest maximum Y value based on polynomial behavior
 */
export function suggestYMax(polynomial) {
  if (!polynomial?.expandedCoefficients) return 10;

  const roots = polynomial.factors.map(f => f.root).sort((a, b) => a - b);
  const samplePoints = [
    ...roots,
    ...roots.slice(0, -1).map((r, i) => (r + roots[i + 1]) / 2),
    roots[0] - 1,
    roots[roots.length - 1] + 1,
  ];

  const yValues = samplePoints.map(x => evaluatePolynomialAt(polynomial, x));
  const maxY = Math.max(...yValues, polynomial.yIntercept);

  return Math.min(50, Math.ceil(maxY * 1.2));
}

// ============================================
// SEEDED RANDOM UTILITIES
// ============================================

/**
 * Seeded random shuffle for consistent graph ordering
 */
function seededShuffle(array, seed) {
  const rng = createSeededRandom(seed);
  const shuffled = [...array];
  let originalIndex = 0; // Track where index 0 ends up

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];

    // Track the original first element
    if (originalIndex === i) originalIndex = j;
    else if (originalIndex === j) originalIndex = i;
  }

  return { shuffled, originalIndex };
}

/**
 * Create a seeded random number generator (LCG algorithm)
 */
function createSeededRandom(seed) {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

// ============================================
// LATEX CONVERSION
// ============================================

/**
 * Convert equation string to LaTeX for preview
 *
 * Handles:
 * - Exponents: ^2 -> ^{2}
 * - Positive fractions: 1/6 -> \frac{1}{6}
 * - Negative fractions: -1/6 -> -\frac{1}{6}
 * - Decimal coefficients preserved as-is
 */
export function equationToLatex(equation) {
  if (!equation) return '';

  let latex = equation;

  // Handle exponents: ^2 -> ^{2}, ^10 -> ^{10}
  latex = latex.replace(/\^(\d+)/g, '^{$1}');

  // Handle negative fractions: -1/6 -> -\frac{1}{6}
  latex = latex.replace(/-(\d+)\/(\d+)/g, '-\\frac{$1}{$2}');

  // Handle positive fractions: 1/6 -> \frac{1}{6}
  // Use negative lookbehind to avoid matching already-processed negative fractions
  latex = latex.replace(/(?<!-)(\d+)\/(\d+)/g, '\\frac{$1}{$2}');

  return latex;
}
